import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Text,
  Flex,
  Tabs,
  Tab,
  Card,
  Link,
  Table,
  TableCell,
  TableRow,
  TableBody,
  SlideoutDrawerV2,
} from "@jsq/ds/core";
import { Sizes } from "@jsq/ds/styles";
import { formatError, useReportError } from "#common/hooks/useReportError";
import { useGetPaymentBatchLogsLazyQuery } from "#graphql/supergraphHooks";
import { clientContext, toDbId } from "#utils/graphql_utils";
import Money from "../../../Money";
import { PositionsTable } from "../../../PositionsTable";
import { CombinedStatusRenderer } from "../../../shared/CombinedStatusRenderer";
import { LogsList } from "../../../shared/LogsList";
import { buildTransactionUrl } from "../../../shared/transactionUrl";
import { usePaymentsContext } from "../../Context/PaymentsContext";
import { paymentBatchLogsConfig } from "../paymentBatchLogsConfig";
import { PaymentBatchReviewInfoBanner, usePaymentBatchReview } from "../PaymentBatchReview";
import { PaymentOriginationDetailsCard } from "../PaymentOriginationDetailsCard";
import { PaymentBatchDetails } from "../types";
import {
  paymentDetails,
  commonTabs,
  paymentDetailsCard,
  cardTitle,
  headerContainer,
  middleContainer,
  tableRow,
  slideoutHeader,
  statusRow,
  approvalsBannerContainer,
  logContainer,
} from "./styles_vanilla.css";

export enum PaymentDetailsTabs {
  Details = "Overview",
  Positions = "Positions",
  Log = "Log",
}

export type PaymentDetailsDisplayProps = {
  paymentBatchDetails: PaymentBatchDetails;
};

export const PaymentDetailsDisplay: React.FC<PaymentDetailsDisplayProps> = ({
  paymentBatchDetails,
}: PaymentDetailsDisplayProps) => {
  const { isGPX, isM3Enabled } = usePaymentsContext();

  const [currentTab, setCurrentTab] = useState<PaymentDetailsTabs>(PaymentDetailsTabs.Details);

  const [fetchLogs, { data: logsData, loading: logsLoading, error: logsError }] =
    useGetPaymentBatchLogsLazyQuery({
      fetchPolicy: "cache-and-network",
      context: clientContext(),
    });

  useReportError(logsError ? formatError(logsError) : null);

  const logs = logsData?.paymentBatch?.logs || [];

  // Fetch logs when Log tab is selected
  useEffect(() => {
    if (currentTab === PaymentDetailsTabs.Log && !logsData) {
      void fetchLogs({
        variables: {
          paymentBatchId: paymentBatchDetails.id,
        },
      });
    }
  }, [currentTab, logsData, fetchLogs, paymentBatchDetails.id]);

  const handleTabChange = useCallback((tab: PaymentDetailsTabs) => {
    setCurrentTab(tab);
  }, []);

  const entityUrl = useMemo<string>(
    () =>
      `https://${paymentBatchDetails.hostName}/account/${toDbId(paymentBatchDetails.entityId)}`,
    [paymentBatchDetails],
  );

  const isSinglePosition = Boolean(isM3Enabled) && paymentBatchDetails.positionCount === 1;
  const detailsCardTitle = isSinglePosition ? "PAYMENT DETAILS" : "PAYMENT BATCH DETAILS";
  const positionLabel = isSinglePosition ? "Position" : "Positions";

  const transactionUrl = useMemo<string | null>(
    () =>
      paymentBatchDetails.transactionBatchId && paymentBatchDetails.objectType
        ? buildTransactionUrl(
            paymentBatchDetails.objectType,
            paymentBatchDetails.transactionBatchId,
            paymentBatchDetails.hostName,
          )
        : null,
    [paymentBatchDetails],
  );

  const { approveButton, denyButton, denyModal } = usePaymentBatchReview({
    batchId: paymentBatchDetails.id,
    reviewState: paymentBatchDetails.reviewState,
  });

  return (
    <>
      <SlideoutDrawerV2.ComplexHeader
        title={
          isM3Enabled
            ? paymentBatchDetails.entityName
            : paymentBatchDetails.transactionDescription || ""
        }
        subtitles={[
          ...(isM3Enabled && paymentBatchDetails.referenceObjectLabel
            ? [
                <Text key="label" variant="secondary-md">
                  {`${paymentBatchDetails.referenceObjectLabel}${paymentBatchDetails.referenceObjectSubtitleLabel ? ` (${paymentBatchDetails.referenceObjectSubtitleLabel})` : ""}`}
                </Text>,
              ]
            : []),
        ]}
        className={slideoutHeader}
      />
      <Flex className={paymentDetails} flexDirection="column" data-testid="paymentDetailsView">
        <Flex className={headerContainer}>
          <Flex className={statusRow} flexDirection="column" gap={4}>
            <CombinedStatusRenderer config={paymentBatchDetails.combinedStatusConfig} />
            <Text variant="secondary-md">Status</Text>
          </Flex>
          <Flex>
            <Tabs className={commonTabs}>
              <Tab
                role="tab"
                text={PaymentDetailsTabs.Details}
                onClick={() => handleTabChange(PaymentDetailsTabs.Details)}
                active={currentTab === PaymentDetailsTabs.Details}
              ></Tab>
              <Tab
                role="tab"
                text={PaymentDetailsTabs.Positions}
                onClick={() => handleTabChange(PaymentDetailsTabs.Positions)}
                active={currentTab === PaymentDetailsTabs.Positions}
              ></Tab>
              <Tab
                role="tab"
                text={PaymentDetailsTabs.Log}
                onClick={() => handleTabChange(PaymentDetailsTabs.Log)}
                active={currentTab === PaymentDetailsTabs.Log}
              ></Tab>
            </Tabs>
          </Flex>
        </Flex>

        <Flex className={middleContainer}>
          {currentTab === PaymentDetailsTabs.Details && (
            <>
              <div className={approvalsBannerContainer}>
                <PaymentBatchReviewInfoBanner
                  isGPX={isGPX}
                  reviewState={paymentBatchDetails.reviewState}
                />
              </div>
              <PaymentOriginationDetailsCard
                paymentBatchDetails={paymentBatchDetails}
                entityUrl={entityUrl}
              />
              <Card className={paymentDetailsCard}>
                <Flex flexDirection="column">
                  <Text className={cardTitle} variant="secondary-md">
                    {detailsCardTitle}
                  </Text>
                  <Table condensed>
                    <TableBody>
                      <TableRow className={tableRow}>
                        <TableCell>
                          <Text variant="secondary-md">Transaction description</Text>
                        </TableCell>
                        <TableCell>
                          {transactionUrl ? (
                            <Link
                              href={transactionUrl}
                              align="left"
                              icon="external-link"
                              iconPosition="right"
                              shouldOpenInNewTab
                            >
                              {paymentBatchDetails.transactionDescription || ""}
                            </Link>
                          ) : (
                            <Text variant="regular-md">
                              {paymentBatchDetails.transactionDescription || ""}
                            </Text>
                          )}
                        </TableCell>
                      </TableRow>
                      {!isM3Enabled && (
                        <TableRow className={tableRow}>
                          <TableCell>
                            <Text variant="secondary-md">Batch created date</Text>
                          </TableCell>
                          <TableCell>
                            <Text variant="regular-md">{paymentBatchDetails.createdDate}</Text>
                          </TableCell>
                        </TableRow>
                      )}
                      {paymentBatchDetails.scheduledProcessDate && (
                        <TableRow className={tableRow}>
                          <TableCell>
                            <Text variant="secondary-md">Payment date</Text>
                          </TableCell>
                          <TableCell>
                            <Text variant="regular-md">
                              {paymentBatchDetails.scheduledProcessDate}
                            </Text>
                          </TableCell>
                        </TableRow>
                      )}
                      {isM3Enabled && !isGPX && paymentBatchDetails.requestedPaymentDate && (
                        <TableRow className={tableRow}>
                          <TableCell>
                            <Text variant="secondary-md">Requested payment date</Text>
                          </TableCell>
                          <TableCell>
                            <Text variant="regular-md">
                              {paymentBatchDetails.requestedPaymentDate}
                            </Text>
                          </TableCell>
                        </TableRow>
                      )}
                      <TableRow className={tableRow}>
                        <TableCell>
                          <Text variant="secondary-md">Payment amount</Text>
                        </TableCell>
                        <TableCell>
                          <Money money={paymentBatchDetails.totalAmount} rounded={false} />
                        </TableCell>
                      </TableRow>
                      <TableRow className={tableRow}>
                        <TableCell>
                          <Text variant="secondary-md">{positionLabel}</Text>
                        </TableCell>
                        <TableCell>
                          {isSinglePosition && paymentBatchDetails.singlePositionId ? (
                            <Link
                              href={`https://${paymentBatchDetails.hostName}/position/${toDbId(paymentBatchDetails.singlePositionId)}`}
                              align="left"
                              icon="external-link"
                              iconPosition="right"
                              shouldOpenInNewTab
                            >
                              {paymentBatchDetails.singlePositionName ?? "View position"}
                            </Link>
                          ) : (
                            <Text variant="regular-md" data-testid="positions-value">
                              {!isSinglePosition &&
                              paymentBatchDetails.totalPositionsInDistribution
                                ? `${paymentBatchDetails.receivingAccount} of ${paymentBatchDetails.totalPositionsInDistribution}`
                                : paymentBatchDetails.receivingAccount}
                            </Text>
                          )}
                        </TableCell>
                      </TableRow>
                      {(!isSinglePosition || paymentBatchDetails.paymentDescription) && (
                        <TableRow className={tableRow}>
                          <TableCell>
                            <Text variant="secondary-md">
                              Payment description for all recipients
                            </Text>
                          </TableCell>
                          <TableCell>
                            <Text variant="regular-md">
                              {paymentBatchDetails.paymentDescription || "None"}
                            </Text>
                          </TableCell>
                        </TableRow>
                      )}
                      {!isGPX && paymentBatchDetails.makerNote && (
                        <TableRow className={tableRow}>
                          <TableCell>
                            <Text variant="secondary-md">Notes for reviewer</Text>
                          </TableCell>
                          <TableCell>
                            <Text variant="regular-md">{paymentBatchDetails.makerNote}</Text>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </Flex>
              </Card>
            </>
          )}
          {currentTab === PaymentDetailsTabs.Positions && (
            <PositionsTable
              id={paymentBatchDetails.id}
              payments={paymentBatchDetails.payments}
              batchId={paymentBatchDetails.transactionBatchId}
              hostName={paymentBatchDetails.hostName}
              batchStatus={paymentBatchDetails.status}
            />
          )}
          {currentTab === PaymentDetailsTabs.Log && (
            <Flex flexDirection="column" gap={Sizes.MD} className={logContainer}>
              <LogsList
                logs={logs}
                loading={logsLoading}
                error={logsError}
                config={paymentBatchLogsConfig}
              />
            </Flex>
          )}
        </Flex>
      </Flex>
      <SlideoutDrawerV2.ComplexFooter
        secondaryButton={denyButton}
        primaryButton={approveButton}
      />
      {denyModal}
    </>
  );
};
